<template>
  <div class="raise-report-panel" v-loading="reportLoading">
    <!-- 时间范围选择器 -->
    <div class="time-range-selector">
      <el-select v-model="selectedTimeRange" placeholder="选择时间范围" style="width: 320px" @change="onTaskChange">
        <el-option v-for="timeText in dateTimeRange" :key="timeText" :label="timeText" :value="timeText"></el-option>
      </el-select>

      <el-button type="text" :loading="reportLoading" @click="getRaiseTaskReport">刷新</el-button>
    </div>

    <!-- 预算信息区域 -->
    <div class="budget-section">
      <h3 class="section-title">起量预算(元)</h3>
      <div class="budget-display">
        <span class="budget-amount">{{ formatBudget(currentReport.raiseBudget) }}</span>
        <span class="budget-remain">(余额: {{ formatBudget(currentReport.remainBudget) }})</span>
      </div>
    </div>

    <!-- 关键指标卡片区域 -->
    <div v-if="reportLoading" class="metrics-section loading-section">
      <div class="loading-text">数据加载中...</div>
    </div>
    <div v-else-if="metricItems.length > 0" class="metrics-section">
      <div v-for="metric in metricItems" :key="metric.metricCode" @click="onMetricsCardClick(metric)" class="metric-card">
        <h4 class="metric-title">{{ metric.metricName }}</h4>
        <p class="metric-ratio">{{ metric.raiseValue }}</p>
        <!-- <div class="metric-details">
          <div class="detail-row">
            <span class="detail-label">基础{{ getMetricDisplayName(metric.metricName) }}</span>
            <span class="detail-value">{{ formatMetricValue(metric.baseValue, metric.metricCode) }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">起量{{ getMetricDisplayName(metric.metricName) }}</span>
            <span class="detail-value">{{ formatMetricValue(metric.raiseValue, metric.metricCode) }}</span>
          </div>
        </div> -->
      </div>
    </div>
    <div v-else class="metrics-section empty-section">
      <div class="empty-text">暂无数据</div>
    </div>

    <!-- 趋势图区域 -->
    <div class="chart-section">
      <h3 class="section-title">趋势图</h3>
      <div class="chart-container">
        <div v-show="!hasChartData" class="empty-chart">暂无数据</div>
        <div v-show="hasChartData" class="line-chart" style="width: 100%; height: 400px" ref="chartRef"></div>
      </div>
    </div>

    <div class="table-section">
      <h3 class="section-title">素材起量明细</h3>

      <el-table border class="summary-el-table-styles common-el-table-styles" :height="330" :data="currentReport.materialDetails">
        <el-table-column prop="name" label="素材">
          <template slot-scope="{ row }">
            <div style="display: flex; align-items: flex-end">
              <div style="min-width: 260px">
                <p>素材名称：{{ row.name }}</p>
                <p>ID：{{ row.materialId }}</p>
              </div>

              <el-image
                v-popover="{ url: row.url, type: row.materialType === 'VIDEO' ? 'video' : 'image', height: 220 }"
                style="height: 42px; cursor: pointer"
                :src="row.coverUrl"
              ></el-image>
            </div>
          </template>
        </el-table-column>
        <el-table-column sortable prop="cost" label="消耗（元）" width="140"></el-table-column>
        <el-table-column sortable prop="convertCount" label="转化数" width="140"></el-table-column>
        <el-table-column sortable prop="convertCost" label="转化成本" width="140"></el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script setup>
import Echarts from 'echarts'
import { ref, computed, defineProps, nextTick, onMounted } from 'vue'
import { getPtomPlanToutiaoRaiseReport } from '@/api/ptom.data.model'
import getChartOptions from './chart-opts.js'

const props = defineProps({
  planId: {
    type: Number,
    default: 0
  }
})

const selectedTimeRange = ref('')
const reportLoading = ref(false)
const hasChartData = ref(true)
const reportData = ref([])
const chartRef = ref(null)

const dateTimeRange = computed(() => {
  return reportData.value?.length ? reportData.value.map((i) => i.timeRangeText) : []
})
const currentReport = computed(() => {
  return reportData.value.find((i) => i.timeRangeText === selectedTimeRange.value) || {}
})

// 指标数据转换 - 直接使用接口返回的数组
const metricItems = computed(() => {
  return currentReport.value.overviewMetrics || []
})

let chartInstance = null
const initChart = () => {
  if (chartRef.value) {
    chartInstance = Echarts.init(chartRef.value)
  }
}

// 获取报表数据
const getRaiseTaskReport = () => {
  reportLoading.value = true
  getPtomPlanToutiaoRaiseReport({ planId: props.planId })
    .then((data) => {
      if (data?.length) {
        reportData.value = data
        nextTick(() => {
          selectedTimeRange.value = data[0].timeRangeText || ''
          if (chartInstance) {
            const [firstMetric] = metricItems.value
            onMetricsCardClick(firstMetric)
          }
        })
      }
    })
    .catch((error) => {
      console.error('获取报表数据失败:', error)
    })
    .finally(() => {
      reportLoading.value = false
    })
}

// 组件加载时获取数据
getRaiseTaskReport()

onMounted(() => {
  initChart()
})

const onMetricsCardClick = (metric) => {
  const data = currentReport.value.trendChart[metric.metricCode]
  hasChartData.value = !!data?.length
  if (hasChartData.value && chartInstance) {
    chartInstance.setOption(getChartOptions({ data, title: getChartTitle(metric.metricCode) }))
  }
}

// 获取指标显示名称（去掉括号内容）
const getMetricDisplayName = (metricName) => {
  if (!metricName) return ''
  // 例如："转化数(计费时间)" -> "转化"
  return metricName.replace(/\(.*?\)/g, '').trim()
}

// 格式化预算金额 - 两位小数
const formatBudget = (value) => {
  const num = Number(value || 0)
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
}

function onTaskChange() {
  const [firstMetric] = metricItems.value
  onMetricsCardClick(firstMetric)
}

function getChartTitle(prop) {
  switch (prop) {
    case 'costPoints':
      return '消耗（元）'
    case 'convertPoints':
      return '转化'

    case 'attributionConvertPoints':
      return '转化数(计费时间)'

    case 'clickPoints':
      return '点击'

    case 'showPoints':
      return '展示'
    default:
      return ''
  }
}
</script>

<style scoped lang="scss">
.raise-report-panel {
  width: 100%;
  padding: 24px;
  background: #f5f7fa;
  min-height: 100%;
}

// 时间范围选择器
.time-range-selector {
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
}

// 区域标题
.section-title {
  margin: 0 0 16px;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

// 预算信息区域
.budget-section {
  margin-bottom: 24px;
  padding: 24px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  .budget-display {
    display: flex;
    align-items: baseline;
    gap: 12px;
  }

  .budget-amount {
    font-size: 48px;
    font-weight: 700;
    color: #303133;
    line-height: 1;
  }

  .budget-remain {
    font-size: 16px;
    color: #909399;
  }
}

// 关键指标卡片区域
.metrics-section {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
  margin-bottom: 24px;
  cursor: pointer;
}

// 加载状态
.loading-section {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;

  .loading-text {
    font-size: 14px;
    color: #909399;
  }
}

// 空数据状态
.empty-section {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;

  .empty-text {
    font-size: 14px;
    color: #c0c4cc;
  }
}

.metric-card {
  padding: 20px;
  background: #f5f7fa;
  border-radius: 10px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  &:hover {
    transform: translateY(-2px);
    background: #fafafa;
  }

  .metric-title {
    margin: 0 0 12px;
    font-size: 14px;
    font-weight: 700;
    color: #606266;
  }

  .metric-ratio {
    margin: 0 0 16px;
    font-size: 28px;
    font-weight: 700;
    color: #303133;
    line-height: 1;
  }

  .metric-details {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .detail-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 13px;
  }

  .detail-label {
    color: #909399;
  }

  .detail-value {
    color: #606266;
    font-weight: 500;
  }
}

// 趋势图区域
.chart-section,
.table-section {
  padding: 24px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  margin-bottom: 24px;

  .chart-container {
    margin-top: 16px;
    position: relative;
    min-height: 120px;

    .empty-chart {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      font-size: 12px;
      color: #909399;
      z-index: 1;
    }
  }

  .chart-header {
    margin-bottom: 16px;
  }

  .chart-metric-name {
    font-size: 14px;
    color: #606266;
    font-weight: 500;
  }

  .chart-area {
    display: flex;
    gap: 16px;
  }

  .y-axis {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 8px 0;
    width: 40px;
    flex-shrink: 0;

    .y-label {
      font-size: 12px;
      color: #909399;
      text-align: right;
    }
  }

  .chart-content {
    flex: 1;
    position: relative;
    height: 320px;
    border: 1px solid #ebeef5;
    border-radius: 8px;
    overflow: hidden;
    background: #fafafa;
  }

  .grid-lines {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
  }

  .grid-line {
    height: 20%;
    border-bottom: 1px solid #ebeef5;

    &:last-child {
      border-bottom: none;
    }
  }

  .chart-placeholder {
  }
}

// 响应式布局
@media (max-width: 1600px) {
  .metrics-section {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 1200px) {
  .metrics-section {
    grid-template-columns: repeat(2, 1fr);
  }

  .budget-section .budget-amount {
    font-size: 36px;
  }
}

@media (max-width: 768px) {
  .raise-report-panel {
    padding: 16px;
  }

  .metrics-section {
    grid-template-columns: 1fr;
  }

  .budget-section {
    padding: 16px;
  }

  .budget-section .budget-amount {
    font-size: 28px;
  }

  .chart-section {
    padding: 16px;
  }

  .chart-content {
    height: 240px !important;
  }
}
</style>
